import { environment } from '../environment';
import { Injectable, signal } from '@angular/core';
import { handleError, HttpError } from '../errors';
import { getAccessToken } from './auth';
import {
  AuditResult,
  Client, ClientDetails,
  ClientEntryList,
  ClientList,
  ClientSecrets,
  CustomerAccount,
  CustomerAccountInput,
  CustomerAccountList,
  CustomerSearchCriteria, isAuditResult,
  isClient, isClientDetails,
  isClientEntryList,
  isClientList,
  isClientSecrets,
  isCustomerAccount,
  isCustomerAccountList,
  isPermissionList,
  isRole,
  isRoleDetails,
  isRoleList,
  isRoleMapperList,
  isRoleMember, isRoleMemberList,
  isScopeEvaluation,
  isSsuList,
  isTestUser,
  isTestUserList,
  isTokenMapper,
  isTokenMapperList,
  isUserCredentials, isUserSearchResultList,
  NewManagementRoleMember,
  NewRoleMember,
  PermissionList,
  Role,
  RoleDetails,
  RoleInput,
  RoleList,
  RoleMapperList, RoleMapperListInput,
  RoleMember, RoleMemberList,
  ScopeEvaluation,
  SsuList,
  TestUser,
  TestUserInput,
  TestUserList,
  TokenMapper,
  TokenMapperInput,
  TokenMapperList,
  UserCredentials,
  UserSearchResultList,
} from './types';

// https://id.sick.com/auth/realms/sickservices/serviceportal/
const baseUrl = environment.authUrl + 'realms/sickservices/serviceportal/';

export interface RequestErrorBody {
  error?: string;
}

export class RequestError extends HttpError {
  body: RequestErrorBody;

  constructor(response: Response, body: unknown) {
    super(response.status, response.statusText);

    this.body = body as RequestErrorBody;
  }
}

@Injectable()
export class ServicePortalApi {
  supportView = signal(false);

  public getClientList(callback: (result: ClientEntryList) => void) {
    this.request('GET', 'client-list', isClientEntryList).then(callback, handleError);
  }

  // Client manager functions

  // Client
  public getManagedClients(callback: (result: ClientList) => void) {
    this.request('GET', 'clients', isClientList).then(callback, handleError);
  }

  public getAudit(all: boolean, callback: (result: AuditResult) => void) {
    const allParam = all ? 'true' : 'false';
    this.request('GET', 'clients/audit?all=' + allParam, isAuditResult).then(callback, handleError);
  }

  public createClient(client: Client, callback: (result: Client) => void,
                      errorCallback: (error: HttpError) => void) {
    this.request('POST', 'clients', isClient, client).then(callback, (error) => {
      if (error instanceof HttpError) {
        errorCallback(error);
      } else {
        handleError(error);
      }
    });
  }

  // implement erroCallback in component, to hide ui elements
  public getClient(id: string, callback: (result: Client) => void, errorCallback: (error: unknown) => void) {
    this.request('GET', 'clients/' + id, isClient).then(callback, errorCallback);
  }

  public updateClient(id: string, body: Client, callback: (result: Client) => void) {
    this.request('PUT', 'clients/' + id, isClient, body).then(callback, handleError);
  }

  public deleteClient(id: string, callback: () => void) {
    this.request('DELETE', 'clients/' + id).then(callback, handleError);
  }

  // Client details (bulk)
  public getClientDetails(id: string,
                          callback: (result: ClientDetails) => void,
                          errorCallback: (error: RequestError) => void) {
    this.request('GET', 'clients/' + id + '/details', isClientDetails).then(callback, (error) => {
      if (error instanceof RequestError) {
        errorCallback(error);
      } else {
        handleError(error);
      }
    });
  }

  // Client roles
  public getClientRoles(id: string, callback: (result: RoleList) => void) {
    this.request('GET', 'clients/' + id + '/roles', isRoleList).then(callback, handleError);
  }

  public createClientRole(id: string, body: RoleInput, callback: (result: Role) => void) {
    this.request('POST', 'clients/' + id + '/roles', isRole, body).then(callback, handleError);
  }

  public getClientRoleDetails(id: string, roleId: string,
                              first: number, size: number,
                              callback: (result: RoleDetails) => void) {
    this.request('GET', 'clients/' + id + '/roles/' + roleId + '/details',
      isRoleDetails, null, { first: first.toString(), size: size.toString() })
      .then(callback, handleError);
  }

  public getClientRoleMembers(id: string, roleId: string,
                              first: number, size: number,
                              callback: (result: RoleMemberList) => void) {
    this.request('GET', 'clients/' + id + '/roles/' + roleId + '/members',
      isRoleMemberList, null, { first: first.toString(), size: size.toString() })
      .then(callback, handleError);
  }

  public updateClientRole(id: string, roleId: string, body: RoleInput, callback: (result: Role) => void) {
    this.request('PUT', 'clients/' + id + '/roles/' + roleId, isRole, body).then(callback, handleError);
  }

  public deleteClientRole(id: string, roleId: string, callback: () => void) {
    this.request('DELETE', 'clients/' + id + '/roles/' + roleId).then(callback, handleError);
  }

  // Client adminroles
  // noinspection JSUnusedGlobalSymbols
  public getAdminPermissionRoles(id: string, callback: (result: PermissionList) => void) {
    this.request('GET', 'clients/' + id + '/permissions', isPermissionList).then(callback, handleError);
  }

  public updateAdminPermissionRole(
    id: string,
    userId: string,
    body: NewManagementRoleMember,
    callback: (result: RoleMember) => void,
  ) {
    this.request('PUT', 'clients/' + id + '/permissions/' + userId, isRoleMember, body).then(callback, handleError);
  }

  public deleteAdminPermissionRoleMember(id: string, roleId: string, userId: string, callback: () => void) {
    this.request('DELETE', 'clients/' + id + '/permissions/' + userId).then(callback, handleError);
  }

  public createAdminPermissionRoleMember(
    id: string,
    body: NewManagementRoleMember,
    callback: (result: RoleMember) => void,
    notFoundCallback: (status: number) => void,
  ) {
    this.request('POST', 'clients/' + id + '/permissions/', isRoleMember, body).then(callback, (error) => {
      if (error instanceof HttpError) {
        if (error.status === 400 || error.status === 404 || error.status === 409 || error.status === 500) {
          notFoundCallback(error.status);
          return;
        }
      }
      handleError(error);
    });
  }

  // Client secrets

  public getClientSecrets(id: string, callback: (result: ClientSecrets) => void) {
    this.request('GET', 'clients/' + id + '/client-secret', isClientSecrets).then(callback, handleError);
  }

  public rotateSecrets(id: string, callback: (result: ClientSecrets) => void) {
    this.request('POST', 'clients/' + id + '/client-secret', isClientSecrets).then(callback, handleError);
  }

  public removeRotatedSecret(id: string, callback: (result: ClientSecrets) => void) {
    this.request('DELETE', 'clients/' + id + '/client-secret/rotated', isClientSecrets).then(callback, handleError);
  }

  public getScopeEvaluation(
    clientId: string,
    userId: string,
    scope: string,
    callback: (result: ScopeEvaluation) => void,
  ) {
    this.request(
      'GET',
      'clients/' + clientId + '/scope-evaluation?userId=' + userId + '&scope=' + encodeURI(scope),
      isScopeEvaluation,
    ).then(callback, handleError);
  }

  // noinspection JSUnusedGlobalSymbols
  public getClientsThatMapRole(id: string, roleId: string, callback: (result: ClientList) => void) {
    this.request('GET', 'clients/' + id + '/roles/' + roleId + '/mappedby', isClientList).then(callback, handleError);
  }


  public createClientRoleMember(
    id: string,
    roleId: string,
    body: NewRoleMember,
    callback: (result: RoleMember) => void,
    notFoundCallback: () => void,
  ) {
    this.request('POST', 'clients/' + id + '/roles/' + roleId + '/members', isRoleMember, body).then(
      callback,
      (error) => {
        if (error instanceof HttpError) {
          if (error.status === 400 || error.status === 500) {
            notFoundCallback();
            return;
          }
        }
        handleError(error);
      },
    );
  }

  public deleteClientRoleMember(id: string, roleId: string, userId: string, callback: () => void) {
    this.request('DELETE', 'clients/' + id + '/roles/' + roleId + '/members/' + userId).then(callback, handleError);
  }

  // Client token mappers
  // noinspection JSUnusedGlobalSymbols
  public getTokenMappers(id: string, callback: (result: TokenMapperList) => void) {
    this.request('GET', 'clients/' + id + '/mappers', isTokenMapperList).then(callback, handleError);
  }

  public createTokenMapper(id: string, body: TokenMapperInput, callback: (result: TokenMapper) => void) {
    this.request('POST', 'clients/' + id + '/mappers', isTokenMapper, body).then(callback, handleError);
  }

  public getTokenMapper(clientId: string, mapperId: string, callback: (result: TokenMapper) => void) {
    this.request('GET', 'clients/' + clientId + '/mappers/' + mapperId, isTokenMapper).then(callback, handleError);
  }

  public updateTokenMapper(
    clientId: string,
    mapperId: string,
    body: TokenMapperInput,
    callback: (result: TokenMapper) => void,
  ) {
    this.request('PUT', 'clients/' + clientId + '/mappers/' + mapperId, isTokenMapper, body).then(
      callback,
      handleError,
    );
  }

  public deleteTokenMapper(clientId: string, mapperId: string, callback: () => void) {
    this.request('DELETE', 'clients/' + clientId + '/mappers/' + mapperId).then(callback, handleError);
  }

  // noinspection JSUnusedGlobalSymbols
  public getRoleMappers(clientId: string, callback: (result: RoleMapperList) => void) {
    this.request('GET', 'clients/' + clientId + '/role-mappers', isRoleMapperList).then(callback, handleError);
  }

  public updateRoleMappers(clientId: string, body: RoleMapperListInput, callback: (result: RoleMapperList) => void) {
    this.request('PUT', 'clients/' + clientId + '/role-mappers', isRoleMapperList, body).then(callback, handleError);
  }

  // noinspection JSUnusedGlobalSymbols
  public getAvailableRoleMappers(clientId: string, callback: (result: RoleMapperList) => void) {
    this.request('GET', 'clients/' + clientId + '/role-mappers/available', isRoleMapperList).then(
      callback,
      handleError,
    );
  }

  // Customer account manager
  public searchCustomers(query: string, callback: (result: CustomerAccountList) => void) {
    this.request('GET', 'customers', isCustomerAccountList, undefined, { query }).then(callback, handleError);
  }

  public exportCustomers(criteria: CustomerSearchCriteria,
                         callback: (result: CustomerAccountList) => void,
                         errorCallback: (error: Error) => void) {
    this.request('POST', 'customers/export', isCustomerAccountList, criteria).then(callback, errorCallback);
  }

  public createCustomer(input: CustomerAccountInput,
                        callback: (result: CustomerAccount) => void,
                        errorCallback: (error: HttpError) => void) {
    this.request('POST', 'customers', isCustomerAccount, input).then(callback,
      (error) => {
        if (error instanceof HttpError) {
          errorCallback(error);
        } else {
          handleError(error);
        }
      });
  }

  public getCustomer(userId: string, callback: (result: CustomerAccount) => void) {
    this.request('GET', 'customers/' + userId, isCustomerAccount).then(callback, handleError);
  }

  public updateCustomer(userId: string, input: CustomerAccountInput, callback: (result: CustomerAccount) => void,
                        errorCallback: (error: HttpError) => void) {
    this.request('PUT', 'customers/' + userId, isCustomerAccount, input).then(callback,
      (error) => {
        if (error instanceof HttpError) {
          errorCallback(error);
        } else {
          handleError(error);
        }
      });
  }

  public deleteCustomer(userId: string, callback: () => void) {
    this.request('DELETE', 'customers/' + userId).then(callback, handleError);
  }

  public updateCustomerCredentials(
    userId: string,
    credentials: UserCredentials,
    callback: (result: UserCredentials) => void,
  ) {
    this.request('PUT', 'customers/' + userId + '/credentials', isUserCredentials, credentials).then(
      callback,
      handleError,
    );
  }

  public getSsus(callback: (result: SsuList) => void) {
    this.request('GET', 'customers/ssus', isSsuList).then(callback, handleError);
  }

  // Test user manager
  public getTestUsers(callback: (result: TestUserList) => void) {
    this.request('GET', 'test-users', isTestUserList).then(callback, handleError);
  }

  public createTestUser(input: TestUserInput, callback: (result: TestUser) => void,
                        errorCallback: (error: HttpError) => void) {
    this.request('POST', 'test-users', isTestUser, input).then(callback,
      (error) => {
        if (error instanceof HttpError) {
          errorCallback(error);
        } else {
          handleError(error);
        }
      });
  }

  public getTestUser(id: string, callback: (result: TestUser) => void) {
    this.request('GET', 'test-users/' + id, isTestUser).then(callback, handleError);
  }

  public updateTestUser(id: string, input: TestUserInput, callback: (result: TestUser) => void,
                        errorCallback: (error: RequestError) => void) {
    this.request('PUT', 'test-users/' + id, isTestUser, input)
      .then(callback, (error) => {
        if (error instanceof RequestError) {
          errorCallback(error);
        } else {
          handleError(error);
        }
      });
  }

  public deleteTestUser(id: string, callback: () => void) {
    this.request('DELETE', 'test-users/' + id).then(callback, handleError);
  }

  public updateTestUserCredentials(
    userId: string,
    credentials: UserCredentials,
    callback: () => void,
  ) {
    this.request('PUT', 'test-users/' + userId + '/credentials', undefined, credentials).then(
      callback,
      handleError,
    );
  }

  public searchUsers(search: string, callback: (result: UserSearchResultList) => void) {
    this.request('GET', 'users', isUserSearchResultList, null, { search })
      .then(callback, handleError);
  }

  async request<T>(
    method: string,
    path: string,
    typeCheckResponse?: (obj: unknown) => obj is T,
    requestBody?: unknown,
    searchParams?: Record<string, string>,
  ): Promise<T> {
    const url = new URL(baseUrl + path);
    if (searchParams) {
      for (const key in searchParams) {
        const value = searchParams[key];
        url.searchParams.set(key, value);
      }
    }

    const headers: HeadersInit = {
      Accept: 'application/json',
      ['Content-Type']: 'application/json',
      Authorization: 'Bearer ' + (await getAccessToken()),
    };
    if (this.supportView()) {
      headers['X-Sickid-Support-View'] = 'true';
    }

    const response = await fetch(url, {
      method,
      headers,
      body: requestBody ? JSON.stringify(requestBody) : null,
    });
    if (!response.ok) {
      let body: unknown = undefined;
      try {
        body = await response.json();
      } catch (_e) {
        // Ignore
      }
      throw new RequestError(response, body);
    }

    if (!typeCheckResponse) {
      return undefined as T;
    }

    const body = await response.json();

    if (typeCheckResponse(body)) {
      return body;
    }
    throw new Error(`Response did not pass type check ${typeCheckResponse.name}`, { cause: body });
  }
}
